'use client';

import { usePathname } from 'next/navigation';

import type { BreadCumbNode } from '../headers/BreadCumb';
import BreadCumb from '../headers/BreadCumb';

// Define the allowed paths that should appear in breadcrumbs
const ALLOWED_PATHS = ['blogs', 'categories', 'search'];

// Function to make path segments more readable
function formatSegmentTitle(segment: string): string {
  // Convert slug format to readable text with proper capitalization
  const words = segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  // Map specific paths to more user-friendly titles
  const pathTitles: Record<string, string> = {
    blogs: 'Bài viết',
    categories: 'Danh Mục',
    search: 'Tìm Kiếm',
  };

  return pathTitles[segment] || words;
}

function useBreadcrumb(): BreadCumbNode[] {
  const path = usePathname();
  if (!path) return [];

  const segments = path.split('/').filter(Boolean);
  const breadcrumb: BreadCumbNode[] = [];

  // Start with home
  breadcrumb.push({ title: 'Trang chủ', link: '/' });

  // Add blog as the second level if present
  if (segments[0] === 'blogs') {
    breadcrumb.push({ title: 'Bài viết', link: '/blogs/p/1/' });
  }

  // Process remaining segments
  for (let i = 1; i < segments.length; i += 1) {
    const segment = segments[i];
    const fullPath = `/${segments.slice(0, i + 1).join('/')}`;

    // Only add specific allowed paths or final segment with no link
    if (segment && ALLOWED_PATHS.includes(segment)) {
      breadcrumb.push({
        title: formatSegmentTitle(segment || ''),
        link: fullPath,
      });
    } else if (i === segments.length - 1) {
      // For the final segment that's not in allowed paths (like a slug)
      // Show as current page without link
      breadcrumb.push({
        title: formatSegmentTitle(segment || ''),
        link: undefined, // Current page has no link
      });
    }
  }

  return breadcrumb;
}

export default function BreadcrumbWrapper() {
  const nodes = useBreadcrumb();

  // Don't render if there's only the home page in breadcrumb
  if (nodes.length <= 1) return null;

  return (
    <div className="section-container mx-auto mt-5 md:mt-8">
      <BreadCumb nodes={nodes} />
    </div>
  );
}
